<script setup>
import { ref, watch, computed, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';
import { useOrderStatus } from '@/composables/useOrderStatus.js';
import { useToast } from 'primevue/usetoast';
import Divider from 'primevue/divider';
import axios from 'axios';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { formatCents } = useCurrencyFormatter();
const { getStatusLabel, getStatusSeverity, getCancellationTooltip } = useOrderStatus();
const toast = useToast();

const props = defineProps({
    visible: Boolean,
    orderId: { type: Number, default: null },
});
const emit = defineEmits(['update:visible']);

const dialogVisible = computed({
    get: () => props.visible,
    set: (val) => emit('update:visible', val),
});

const loading = ref(false);
const details = ref(null);

// Plain date strings (YYYY-MM-DD) must be parsed manually to avoid UTC midnight timezone shift
const formatDate = (date) => {
    if (!date) return '-';
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const [y, m, d] = date.split('-');
        return new Date(y, m - 1, d).toLocaleDateString('pt-BR');
    }
    return new Date(date).toLocaleDateString('pt-BR');
};

const formatMoney = (cents, coin) => {
    if (cents == null || !coin) return '-';
    return formatCents(cents, coin);
};

let abortController = null;

const fetchDetails = async ({ reset = false } = {}) => {
    const id = props.orderId;
    if (!id) return;

    abortController?.abort();
    abortController = new AbortController();

    if (reset && details.value?.id !== id) details.value = null;

    loading.value = true;
    try {
        const url = proxy.route ? proxy.route('orders.details', id) : `/admin/orders/${id}/details`;
        const res = await axios.get(url, { signal: abortController.signal });
        details.value = res.data;
    } catch (err) {
        if (!axios.isCancel(err)) {
            toast.add({ severity: 'error', summary: $t('Error'), detail: $t('Failed to load order details'), life: 3000 });
            emit('update:visible', false);
        }
    } finally {
        loading.value = false;
    }
};

watch(
    () => [props.visible, props.orderId],
    async ([vis, id]) => {
        if (!vis || !id) return;
        await fetchDetails({ reset: true });
    }
);

const clientName = computed(() => {
    if (!details.value?.client) return '-';
    const { first_name, last_name, name } = details.value.client;
    return first_name && last_name ? `${first_name} ${last_name}` : name || '-';
});

const itemsWithContentOrPublication = computed(() =>
    (details.value?.items || []).filter((i) => i.content || i.link || i.live_link_confirmed_at)
);

const statusOverview = computed(() => {
    if (!details.value) return [];
    const items = details.value.items || [];
    const itemsWithContent = items.filter(i => i.content);

    // Conteúdo — pendente se algum item com conteúdo não confirmado
    let contentDate = null;
    let contentPending = false;
    if (itemsWithContent.length > 0) {
        contentPending = itemsWithContent.some(i => !i.content?.confirmed_by_admin_at);
        if (!contentPending) {
            contentDate = itemsWithContent
                .map(i => i.content?.confirmed_by_admin_at).filter(Boolean).sort().pop();
        }
    }

    // Publicação — pendente se algum item sem live_link_confirmed_at
    let pubDate = null;
    let pubPending = items.length > 0;
    if (items.length > 0 && items.every(i => i.live_link_confirmed_at)) {
        pubPending = false;
        pubDate = items.map(i => i.live_link_confirmed_at).filter(Boolean).sort().pop();
    }

    const allPaid = items.length > 0 && items.every(i => i.paid);
    const inv = details.value.invoice;

    return [
        {
            key: 'payment',
            icon: 'pi-credit-card',
            label: $t('Payment'),
            done: !!details.value.payment_date,
            text: details.value.payment_date ? formatDate(details.value.payment_date) : $t('Pending'),
        },
        {
            key: 'content',
            icon: 'pi-file-edit',
            label: $t('Content'),
            done: itemsWithContent.length > 0 && !contentPending,
            text: itemsWithContent.length === 0 ? '-' : contentPending ? $t('Pending') : formatDate(contentDate),
        },
        {
            key: 'publication',
            icon: 'pi-globe',
            label: $t('Publication'),
            done: !pubPending && items.length > 0,
            text: items.length === 0 ? '-' : pubPending ? $t('Pending') : formatDate(pubDate),
        },
        {
            key: 'supplier',
            icon: 'pi-user',
            label: $t('Supplier payment'),
            done: allPaid,
            text: items.length === 0 ? '-' : allPaid ? $t('Done') : $t('Pending'),
        },
        {
            key: 'invoice',
            icon: 'pi-file-pdf',
            label: $t('Invoice emission'),
            done: !!inv?.initial_invoice_emitted,
            text: !inv ? '-' : inv.initial_invoice_emitted ? formatDate(inv.emitted_at) : $t('Pending'),
        },
    ];
});

const itemCardClasses = (item) => {
    let base;
    if (item.live_link_confirmed_at)          base = { border: 'border-green-300',  header: 'bg-green-50' };
    else if (item.content?.sent_to_portal_at) base = { border: 'border-blue-300',   header: 'bg-blue-50' };
    else if (item.content)                    base = { border: 'border-orange-300',  header: 'bg-orange-50' };
    else                                      base = { border: 'border-surface-200', header: 'bg-surface-50' };

    if (item.canceled_at) {
        return { border: `${base.border} opacity-60`, header: `${base.header} line-through` };
    }
    return base;
};

const expandedItems = ref(new Set());
const toggleItem = (id) => {
    const s = new Set(expandedItems.value);
    s.has(id) ? s.delete(id) : s.add(id);
    expandedItems.value = s;
};

const goToEditOrder = () => router.visit(proxy.route('orders.edit', details.value?.id));
</script>

<template>
    <Dialog
        v-model:visible="dialogVisible"
        modal
        :header="details ? `${$t('Order Details')} #${details.number}` : $t('Order Details')"
        :style="{ width: '900px', maxHeight: '80vh' }"
        :breakpoints="{ '960px': '95vw', '641px': '100vw' }"
        :contentStyle="{ overflowY: 'auto', maxHeight: '70vh' }"
    >
        <div v-if="loading" class="flex justify-center items-center py-16">
            <i class="pi pi-spin pi-spinner text-4xl text-primary-500"></i>
        </div>

        <div v-else-if="details" class="space-y-6 py-1">
            <!-- Summary -->
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-surface-50 rounded-lg border border-surface-200">
                <div>
                    <p class="text-xs font-medium text-surface-500 uppercase mb-1">{{ $t('Number') }}</p>
                    <p class="font-bold text-lg">#{{ details.number }}</p>
                </div>
                <div>
                    <p class="text-xs font-medium text-surface-500 uppercase mb-1">{{ $t('Client') }}</p>
                    <p class="font-semibold">{{ clientName }}</p>
                    <p v-if="details.client?.email" class="text-xs text-surface-500 truncate">{{ details.client.email }}</p>
                </div>
                <div>
                    <p class="text-xs font-medium text-surface-500 uppercase mb-1">{{ $t('Status') }}</p>
                    <Tag :value="getStatusLabel(details.status, $t)" :severity="getStatusSeverity(details.status)" v-tooltip.top="getCancellationTooltip(details, $t)" />
                </div>
                <div>
                    <p class="text-xs font-medium text-surface-500 uppercase mb-1">{{ $t('Created at') }}</p>
                    <p>{{ formatDate(details.created_at) }}</p>
                </div>
            </div>

            <!-- Cancellation details -->
            <div v-if="details.status === 'CANCELED'" class="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p class="text-sm font-semibold text-red-700 mb-1">
                    <i class="pi pi-times-circle mr-1"></i>
                    {{ $t('Order canceled') }}<span v-if="details.canceled_at"> - {{ formatDate(details.canceled_at) }}</span>
                </p>
                <p class="text-sm text-red-900 whitespace-pre-line">
                    {{ details.cancellation_reason || $t('No reason provided') }}
                </p>
            </div>

            <!-- Status Overview -->
            <div class="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <div
                    v-for="item in statusOverview"
                    :key="item.key"
                    class="flex flex-col items-center gap-1 rounded-lg border py-3 px-2 text-center"
                    :class="item.done
                        ? 'bg-green-50 border-green-200'
                        : item.text === '-' ? 'bg-surface-50 border-surface-200' : 'bg-orange-50 border-orange-200'"
                >
                    <i
                        class="pi text-xl"
                        :class="[item.icon, item.done ? 'text-green-500' : item.text === '-' ? 'text-surface-300' : 'text-orange-400']"
                    ></i>
                    <p class="text-xs font-medium text-surface-500">{{ item.label }}</p>
                    <p
                        class="text-xs font-semibold"
                        :class="item.done ? 'text-green-600' : item.text === '-' ? 'text-surface-400' : 'text-orange-500'"
                    >{{ item.text }}</p>
                </div>
            </div>

            <!-- Payments -->
            <div>
                <div class="flex items-center gap-2 px-3 py-2 bg-blue-50 border-l-4 border-blue-400 rounded-r mb-3">
                    <i class="pi pi-credit-card text-blue-500"></i>
                    <h3 class="text-sm font-semibold text-blue-700 uppercase tracking-wide">{{ $t('Payments') }}</h3>
                </div>

                <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                        <p class="text-xs text-surface-500 mb-1">{{ $t('Payment Date') }}</p>
                        <p class="font-medium">{{ formatDate(details.payment_date) }}</p>
                    </div>
                    <div>
                        <p class="text-xs text-surface-500 mb-1">{{ $t('Receipt Date') }}</p>
                        <p class="font-medium">{{ formatDate(details.receipt_date) }}</p>
                    </div>
                    <div>
                        <p class="text-xs text-surface-500 mb-1">{{ $t('Delivery Date') }}</p>
                        <p class="font-medium">{{ formatDate(details.delivery_date) }}</p>
                    </div>
                    <div>
                        <p class="text-xs text-surface-500 mb-1">{{ $t('Invoice') }}</p>
                        <span v-if="details.invoice" class="flex flex-wrap items-center gap-1">
                            <span class="font-medium">#{{ details.invoice.number }}</span>
                            <Tag
                                :value="details.invoice.paid ? $t('Paid') : $t('Pending')"
                                :severity="details.invoice.paid ? 'success' : 'warn'"
                                class="text-xs"
                            />
                        </span>
                        <span v-else class="text-surface-400">-</span>
                    </div>
                </div>

                <div v-if="details.deferred_payment" class="mt-3">
                    <Tag :value="$t('Deferred Payment')" severity="info" />
                </div>

                <div class="mt-4 overflow-x-auto">
                    <table class="text-sm w-full">
                        <thead>
                            <tr class="text-left text-xs text-surface-500 border-b border-surface-200">
                                <th class="pb-2 pr-4">{{ $t('Site') }}</th>
                                <th class="pb-2 pr-4">{{ $t('Sale') }}</th>
                                <th class="pb-2 pr-4">{{ $t('Cost') }}</th>
                                <th class="pb-2 pr-4">{{ $t('Tax') }}</th>
                                <th class="pb-2 text-right"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="item in details.items"
                                :key="item.id"
                                class="border-b border-surface-100 last:border-0"
                            >
                                <td
                                    class="py-2 pr-4 text-xs text-surface-600 max-w-[200px] truncate"
                                    :class="item.canceled_at ? 'line-through opacity-60' : ''"
                                >
                                    {{ item.site_url || '-' }}
                                </td>
                                <td class="py-2 pr-4 font-medium">{{ formatMoney(item.sale, item.sale_coin) }}</td>
                                <td class="py-2 pr-4">{{ formatMoney(item.cost, item.cost_coin) }}</td>
                                <td class="py-2 pr-4">{{ formatMoney(item.payment_tax, item.sale_coin) }}</td>
                                <td class="py-2 text-right whitespace-nowrap">
                                    <Tag v-if="item.canceled_at" :value="$t('Canceled')" severity="danger" class="text-xs" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <Divider />

            <!-- Contents / Publications -->
            <div>
                <div class="flex items-center gap-2 px-3 py-2 bg-amber-50 border-l-4 border-amber-400 rounded-r mb-3">
                    <i class="pi pi-file-edit text-amber-500"></i>
                    <h3 class="text-sm font-semibold text-amber-700 uppercase tracking-wide">{{ $t('Contents') }} / {{ $t('Publications') }}</h3>
                </div>

                <div v-if="itemsWithContentOrPublication.length === 0" class="flex flex-col items-center py-6 text-surface-400">
                    <i class="pi pi-inbox text-3xl mb-2"></i>
                    <span class="text-sm">{{ $t('No content yet') }}</span>
                </div>

                <div v-else class="space-y-4">
                    <div
                        v-for="item in itemsWithContentOrPublication"
                        :key="item.id"
                        class="border rounded-lg overflow-hidden"
                        :class="itemCardClasses(item).border"
                    >
                        <!-- Item header -->
                        <div
                            class="px-4 py-2 flex flex-wrap items-center gap-2 cursor-pointer select-none"
                            :class="itemCardClasses(item).header"
                            @click="toggleItem(item.id)"
                        >
                            <span class="text-sm font-medium text-surface-700 truncate flex-1">{{ item.site_url || '-' }}</span>
                            <Tag v-if="item.type" :value="item.type" severity="secondary" class="text-xs" />
                            <Tag
                                v-if="item.link_status"
                                :value="getStatusLabel(item.link_status, $t)"
                                :severity="getStatusSeverity(item.link_status)"
                                class="text-xs"
                            />
                            <Tag
                                v-if="item.content?.skipped_validation"
                                :value="$t('Sem Validação')"
                                severity="warn"
                                icon="pi pi-exclamation-triangle"
                                class="text-xs"
                                v-tooltip.top="$t('Conteúdo salvo sem validação de âncoras')"
                            />
                            <Tag v-if="item.canceled_at" :value="$t('Canceled')" severity="danger" class="text-xs" />
                            <i
                                class="pi text-surface-400 transition-transform duration-200"
                                :class="expandedItems.has(item.id) ? 'pi-chevron-up' : 'pi-chevron-down'"
                            ></i>
                        </div>

                        <div v-show="expandedItems.has(item.id)" class="p-4 space-y-3">
                            <!-- Anchors -->
                            <div v-if="item.content" class="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                                <div v-if="item.content.anchor_name">
                                    <p class="text-xs text-surface-500 mb-1">{{ $t('Anchor') }} 1</p>
                                    <p class="font-medium">{{ item.content.anchor_name }}</p>
                                    <a
                                        v-if="item.content.anchor_url"
                                        :href="item.content.anchor_url"
                                        target="_blank"
                                        rel="noopener"
                                        class="text-xs text-primary-600 hover:underline break-all"
                                    >{{ item.content.anchor_url }}</a>
                                </div>
                                <div v-if="item.content.anchor_name_2">
                                    <p class="text-xs text-surface-500 mb-1">{{ $t('Anchor') }} 2</p>
                                    <p class="font-medium">{{ item.content.anchor_name_2 }}</p>
                                    <a
                                        v-if="item.content.anchor_url_2"
                                        :href="item.content.anchor_url_2"
                                        target="_blank"
                                        rel="noopener"
                                        class="text-xs text-primary-600 hover:underline break-all"
                                    >{{ item.content.anchor_url_2 }}</a>
                                </div>
                                <div v-if="item.content.anchor_name_3">
                                    <p class="text-xs text-surface-500 mb-1">{{ $t('Anchor') }} 3</p>
                                    <p class="font-medium">{{ item.content.anchor_name_3 }}</p>
                                    <a
                                        v-if="item.content.anchor_url_3"
                                        :href="item.content.anchor_url_3"
                                        target="_blank"
                                        rel="noopener"
                                        class="text-xs text-primary-600 hover:underline break-all"
                                    >{{ item.content.anchor_url_3 }}</a>
                                </div>
                            </div>

                            <!-- Content link + Published link -->
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                <div v-if="item.content?.content_link">
                                    <p class="text-xs text-surface-500 mb-1">{{ $t('Content Link') }}</p>
                                    <a
                                        :href="item.content.content_link"
                                        target="_blank"
                                        rel="noopener"
                                        class="text-xs text-primary-600 hover:underline break-all"
                                    >{{ item.content.content_link }}</a>
                                </div>
                                <div v-if="item.link">
                                    <p class="text-xs text-surface-500 mb-1">{{ $t('Published link') }}</p>
                                    <a
                                        :href="item.link"
                                        target="_blank"
                                        rel="noopener"
                                        class="text-xs text-primary-600 hover:underline break-all"
                                    >{{ item.link }}</a>
                                </div>
                            </div>

                            <!-- Unified timeline: content dates + publication dates -->
                            <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs pt-2 border-t border-surface-100">
                                <div v-if="item.content?.submited_at" class="flex items-start gap-1">
                                    <i class="pi pi-send text-blue-500 mt-0.5 flex-shrink-0"></i>
                                    <div>
                                        <p class="text-surface-500">{{ $t('Submitted') }}</p>
                                        <p class="font-medium">{{ formatDate(item.content.submited_at) }}</p>
                                    </div>
                                </div>
                                <div v-if="item.content?.sent_to_client_approval_at" class="flex items-start gap-1">
                                    <i class="pi pi-user text-indigo-500 mt-0.5 flex-shrink-0"></i>
                                    <div>
                                        <p class="text-surface-500">{{ $t('Sent to client approval') }}</p>
                                        <p class="font-medium">{{ formatDate(item.content.sent_to_client_approval_at) }}</p>
                                    </div>
                                </div>
                                <div v-if="item.content?.client_approved_at" class="flex items-start gap-1">
                                    <i class="pi pi-thumbs-up text-green-600 mt-0.5 flex-shrink-0"></i>
                                    <div>
                                        <p class="text-surface-500">{{ $t('Client approved') }}</p>
                                        <p class="font-medium">{{ formatDate(item.content.client_approved_at) }}</p>
                                    </div>
                                </div>
                                <div v-if="item.content?.client_rejected_at" class="flex items-start gap-1">
                                    <i class="pi pi-thumbs-down text-red-600 mt-0.5 flex-shrink-0"></i>
                                    <div>
                                        <p class="text-surface-500">{{ $t('Client rejected') }}</p>
                                        <p class="font-medium">{{ formatDate(item.content.client_rejected_at) }}</p>
                                    </div>
                                </div>
                                <div v-if="item.content?.confirmed_by_admin_at" class="flex items-start gap-1">
                                    <i class="pi pi-check-circle text-green-500 mt-0.5 flex-shrink-0"></i>
                                    <div>
                                        <p class="text-surface-500">{{ $t('Approved by admin') }}</p>
                                        <p class="font-medium">{{ formatDate(item.content.confirmed_by_admin_at) }}</p>
                                    </div>
                                </div>
                                <div v-if="item.content?.rejected_at" class="flex items-start gap-1">
                                    <i class="pi pi-times-circle text-red-500 mt-0.5 flex-shrink-0"></i>
                                    <div>
                                        <p class="text-surface-500">{{ $t('Rejected') }}</p>
                                        <p class="font-medium">{{ formatDate(item.content.rejected_at) }}</p>
                                    </div>
                                </div>
                                <div v-if="item.content?.revised_at" class="flex items-start gap-1">
                                    <i class="pi pi-refresh text-orange-500 mt-0.5 flex-shrink-0"></i>
                                    <div>
                                        <p class="text-surface-500">{{ $t('Revised') }}</p>
                                        <p class="font-medium">{{ formatDate(item.content.revised_at) }}</p>
                                    </div>
                                </div>
                                <div v-if="item.content?.sent_to_portal_at" class="flex items-start gap-1">
                                    <i class="pi pi-upload text-purple-500 mt-0.5 flex-shrink-0"></i>
                                    <div>
                                        <p class="text-surface-500">{{ $t('Sent to portal') }}</p>
                                        <p class="font-medium">{{ formatDate(item.content.sent_to_portal_at) }}</p>
                                    </div>
                                </div>
                                <div v-if="item.live_link_confirmed_at" class="flex items-start gap-1">
                                    <i class="pi pi-check-circle text-green-600 mt-0.5 flex-shrink-0"></i>
                                    <div>
                                        <p class="text-surface-500">{{ $t('Live link confirmed') }}</p>
                                        <p class="font-medium">{{ formatDate(item.live_link_confirmed_at) }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Order Changes -->
            <template v-if="details.change_logs?.length">
                <Divider />
                <div>
                    <div class="flex items-center gap-2 px-3 py-2 bg-surface-100 border-l-4 border-surface-400 rounded-r mb-3">
                        <i class="pi pi-history text-surface-500"></i>
                        <h3 class="text-sm font-semibold text-surface-700 uppercase tracking-wide">{{ $t('Order changes') }}</h3>
                    </div>

                    <ul class="space-y-3">
                        <li
                            v-for="(log, index) in details.change_logs"
                            :key="index"
                            class="flex items-start gap-3 pb-3 border-b border-surface-100 last:border-0 last:pb-0"
                        >
                            <i
                                class="pi mt-0.5 flex-shrink-0"
                                :class="log.action === 'item_added' ? 'pi-plus-circle text-green-600' : 'pi-minus-circle text-red-600'"
                            ></i>
                            <div class="min-w-0 flex-1">
                                <div class="flex flex-wrap items-center gap-2">
                                    <span
                                        class="text-sm font-medium"
                                        :class="log.action === 'item_added' ? 'text-green-700' : 'text-red-700'"
                                    >
                                        {{ log.action === 'item_added' ? $t('Item added') : $t('Item canceled') }}
                                    </span>
                                    <span v-if="log.site_url" class="text-sm text-surface-700 truncate">{{ log.site_url }}</span>
                                    <span class="text-xs text-surface-400 ml-auto whitespace-nowrap">{{ formatDate(log.at) }}</span>
                                </div>
                                <p v-if="log.detail" class="text-xs text-surface-500 mt-1">{{ log.detail }}</p>
                                <p v-if="log.by" class="text-xs text-surface-400 mt-0.5">{{ $t('By') }}: {{ log.by }}</p>
                            </div>
                        </li>
                    </ul>
                </div>
            </template>
        </div>

        <template #footer>
            <Button
                v-if="details"
                :label="$t('Edit order')"
                icon="pi pi-pencil"
                size="small"
                outlined
                @click="goToEditOrder"
            />
            <Button :label="$t('Close')" severity="secondary" @click="dialogVisible = false" />
        </template>
    </Dialog>
</template>
